/**
 * Stone Cinematic Hero Carousel
 *
 * A luxury multi-image hero with cinematic transitions for Stone Investment.
 * Features:
 * - Ken Burns slow-zoom on each slide
 * - Crossfade transitions between slides
 * - Auto-advancement with pause on hover
 * - Minimal navigation (thin progress bar + arrow buttons)
 * - Image counter
 * - Full-screen lightbox on click
 *
 * Designed for both property detail and development detail heroes.
 */

'use client';

import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface CinematicHeroImage {
  url: string;
  alt?: string | undefined;
  /** When set, this slide renders as a looping background video */
  videoUrl?: string | undefined;
  mimeType?: string | undefined;
}

export interface StoneCinematicHeroCarouselProps {
  images: CinematicHeroImage[];
  height?: string;
  autoPlayInterval?: number;
  children?: React.ReactNode;
  className?: string;
}

export function StoneCinematicHeroCarousel({
  images,
  height = 'h-[70vh]',
  autoPlayInterval = 6000,
  children,
  className = '',
}: StoneCinematicHeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const progressRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const count = images.length;
  const hasMultiple = count > 1;

  const goTo = useCallback(
    (index: number) => {
      setCurrent((index + count) % count);
    },
    [count],
  );

  const goNext = useCallback(() => goTo(current + 1), [current, goTo]);
  const goPrev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Auto-advance
  useEffect(() => {
    if (!hasMultiple || isPaused) return;

    timerRef.current = setTimeout(goNext, autoPlayInterval);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, hasMultiple, isPaused, autoPlayInterval, goNext]);

  // Keyboard navigation
  useEffect(() => {
    if (!hasMultiple) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasMultiple, goNext, goPrev]);

  if (images.length === 0) {
    return (
      <header
        className={`relative w-full ${height} overflow-hidden bg-linear-to-br from-stone-300 to-stone-200 ${className}`}
      >
        {children}
      </header>
    );
  }

  return (
    <>
      <header
        data-dark-hero="true"
        className={`relative isolate w-full ${height} overflow-hidden bg-stone-900 ${className}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Slides */}
        {images.map((img, i) => {
          const isActive = i === current;
          const isVideo = !!img.videoUrl;
          const zoomOrigin = i % 3 === 0 ? 'center' : i % 3 === 1 ? 'top left' : 'bottom right';

          return (
            <div
              key={`${img.videoUrl || img.url}-${i}`}
              className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${
                isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
              onClick={() => {
                if (hasMultiple) {
                  setLightboxIndex(i);
                  setShowLightbox(true);
                }
              }}
              style={{ cursor: hasMultiple ? 'pointer' : undefined }}
            >
              {isVideo ? (
                <video
                  src={img.videoUrl}
                  poster={img.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <Image
                  src={img.url}
                  alt={img.alt || `Image ${i + 1}`}
                  fill
                  className="object-cover"
                  priority={i === 0}
                  sizes="100vw"
                  style={{
                    transformOrigin: zoomOrigin,
                    animation: isActive
                      ? `stoneKenBurns ${autoPlayInterval + 1500}ms ease-out forwards`
                      : undefined,
                  }}
                />
              )}
            </div>
          );
        })}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-linear-to-t from-stone-900/80 via-stone-900/20 to-stone-900/10 z-20 pointer-events-none" />

        {/* Content overlay (title, price, etc.) */}
        {children && <div className="relative z-30">{children}</div>}

        {/* Navigation - only when multiple images */}
        {hasMultiple && (
          <>
            {/* Arrow buttons */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/25 transition-all duration-300"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} className="text-white" strokeWidth={1.5} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/25 transition-all duration-300"
              aria-label="Next image"
            >
              <ChevronRight size={20} className="text-white" strokeWidth={1.5} />
            </button>

            {/* Image counter */}
            <div className="absolute top-6 right-6 z-30 px-3 py-1.5 bg-black/30 backdrop-blur-sm text-white text-xs font-sans tracking-wider">
              {current + 1} / {count}
            </div>

            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 z-30 h-[3px] bg-white/10">
              <div
                ref={progressRef}
                className="h-full bg-white/60"
                style={{
                  width: `${((current + 1) / count) * 100}%`,
                  transition: 'width 500ms ease-out',
                }}
              />
            </div>

            {/* Dot indicators (for <= 8 images) */}
            {count <= 8 && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo(i);
                    }}
                    className={`w-2 h-2 transition-all duration-500 ${
                      i === current
                        ? 'bg-white w-6'
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </header>

      {/* Ken Burns animation */}
      <style jsx global>{`
        @keyframes stoneKenBurns {
          0% {
            transform: scale(1);
          }
          100% {
            transform: scale(1.08);
          }
        }
      `}</style>

      {/* Lightbox */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-9999 bg-stone-950"
          onClick={() => setShowLightbox(false)}
        >
          {/* Close */}
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-6 right-6 z-50 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all text-white"
            aria-label="Close gallery"
          >
            <X size={20} strokeWidth={1.5} />
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 z-50 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white text-xs font-sans tracking-wider">
            {lightboxIndex + 1} / {count}
          </div>

          {/* Arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((lightboxIndex - 1 + count) % count);
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all text-white"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((lightboxIndex + 1) % count);
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 z-50 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all text-white"
            aria-label="Next image"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>

          {/* Image or Video */}
          <div
            className="relative w-full h-full flex items-center justify-center p-12"
            onClick={(e) => e.stopPropagation()}
          >
            {images[lightboxIndex]!.videoUrl ? (
              <video
                src={images[lightboxIndex]!.videoUrl}
                poster={images[lightboxIndex]!.url}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-full"
              />
            ) : (
              <Image
                src={images[lightboxIndex]!.url}
                alt={images[lightboxIndex]!.alt || `Image ${lightboxIndex + 1}`}
                fill
                className="object-contain"
                sizes="100vw"
                priority
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default StoneCinematicHeroCarousel;
